import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
export type PromptVisibility = 'private' | 'team' | 'public';
export type PromptStatus = 'draft' | 'published' | 'archived';
export interface PromptVersion {
  version: number;
  content: string;
  timestamp: string;
}
export interface Prompt {
  id: string;
  name: string;
  slug: string;
  content: string;
  model: string;
  temperature: number;
  tags: string[];
  status: PromptStatus;
  visibility: PromptVisibility;
  isFavorite: boolean;
  versionHistory: PromptVersion[];
  metrics: {
    usageCount: number;
    avgTokens: number;
    successRate: number;
  };
}
interface PromptsState {
  prompts: Prompt[];
  viewMode: 'grid' | 'table';
  activePromptId: string | null;
  addPrompt: (prompt: Prompt) => void;
  updatePrompt: (id: string, updates: Partial<Prompt>) => void;
  deletePrompt: (id: string) => void;
  clonePrompt: (id: string) => void;
  toggleFavorite: (id: string) => void;
  toggleViewMode: () => void;
  setActivePromptId: (id: string | null) => void;
}
const SAMPLE_PROMPTS: Prompt[] = [
  {
    id: 'prompt-1',
    name: 'Sales Email Cold Outreach',
    slug: 'sales-email-cold-outreach',
    content: 'Write a compelling cold outreach email to a potential client in the {{industry}} sector. Mention their recent {{achievement}} and propose a brief meeting to discuss how our {{product}} can help them achieve {{goal}}.',
    model: 'GPT-4o',
    temperature: 0.8,
    tags: ['sales', 'email', 'outreach'],
    status: 'published',
    visibility: 'team',
    isFavorite: true,
    versionHistory: [{ version: 1, content: 'Initial version...', timestamp: new Date().toISOString() }],
    metrics: { usageCount: 1248, avgTokens: 1200, successRate: 96.7 }
  },
  {
    id: 'prompt-2',
    name: 'Technical Support Ticket Response',
    slug: 'tech-support-ticket-response',
    content: 'You are a helpful and patient technical support agent. A customer is reporting an issue: "{{issue_description}}". Acknowledge their problem, provide a potential workaround: "{{workaround}}", and assure them a fix is being worked on.',
    model: 'Claude 3.5 Sonnet',
    temperature: 0.5,
    tags: ['support', 'technical'],
    status: 'published',
    visibility: 'public',
    isFavorite: false,
    versionHistory: [{ version: 1, content: 'Initial version...', timestamp: new Date().toISOString() }],
    metrics: { usageCount: 8502, avgTokens: 850, successRate: 99.1 }
  },
  {
    id: 'prompt-3',
    name: 'Generate Python Boilerplate for API',
    slug: 'python-api-boilerplate',
    content: 'Generate a Python Flask boilerplate for a REST API with endpoints for GET, POST, PUT, and DELETE for a resource named {{resource_name}}. Include basic error handling and a requirements.txt file.',
    model: 'GPT-4o',
    temperature: 0.2,
    tags: ['coding', 'python', 'api'],
    status: 'draft',
    visibility: 'private',
    isFavorite: false,
    versionHistory: [],
    metrics: { usageCount: 56, avgTokens: 2500, successRate: 92.3 }
  }
];
export const usePromptsStore = create<PromptsState>()(
  persist(
    (set) => ({
      prompts: SAMPLE_PROMPTS,
      viewMode: 'grid',
      activePromptId: null,
      addPrompt: (prompt) => set((state) => ({ prompts: [prompt, ...state.prompts] })),
      updatePrompt: (id, updates) => set((state) => ({
        prompts: state.prompts.map((p) => p.id === id ? { ...p, ...updates } : p)
      })),
      deletePrompt: (id) => set((state) => ({
        prompts: state.prompts.filter((p) => p.id !== id)
      })),
      clonePrompt: (id) => set((state) => {
        const target = state.prompts.find((p) => p.id === id);
        if (!target) return state;
        const newPrompt: Prompt = {
          ...target,
          id: `prompt-${crypto.randomUUID()}`,
          name: `${target.name} (Fork)`,
          status: 'draft',
          visibility: 'private',
          isFavorite: false,
          metrics: { usageCount: 0, avgTokens: 0, successRate: 0 },
        };
        return { prompts: [newPrompt, ...state.prompts] };
      }),
      toggleFavorite: (id) => set((state) => ({
        prompts: state.prompts.map((p) => p.id === id ? { ...p, isFavorite: !p.isFavorite } : p)
      })),
      toggleViewMode: () => set((state) => ({ viewMode: state.viewMode === 'grid' ? 'table' : 'grid' })),
      setActivePromptId: (id) => set({ activePromptId: id }),
    }),
    {
      name: 'cloudface-prompts-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
// Primitive selectors
export const usePrompts = () => usePromptsStore((s) => s.prompts);
export const usePromptsViewMode = () => usePromptsStore((s) => s.viewMode);
export const useActivePromptId = () => usePromptsStore((s) => s.activePromptId);
// Action selectors
export const useAddPrompt = () => usePromptsStore((s) => s.addPrompt);
export const useUpdatePrompt = () => usePromptsStore((s) => s.updatePrompt);
export const useDeletePrompt = () => usePromptsStore((s) => s.deletePrompt);
export const useClonePrompt = () => usePromptsStore((s) => s.clonePrompt);
export const useToggleFavoritePrompt = () => usePromptsStore((s) => s.toggleFavorite);
export const useTogglePromptViewMode = () => usePromptsStore((s) => s.toggleViewMode);
export const useSetActivePromptId = () => usePromptsStore((s) => s.setActivePromptId);